import { Button, Icon, Switch, Tooltip, token } from "@may-i/mash-design-system";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Line, Rect, type TPointerEvent, type TPointerEventInfo } from "fabric";
import { CameraScalePopover, ResizeCameraIcon } from "~/widgets/floor-plan/CameraScalePopover";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import type { FloorPlanMemoDiagram, FloorPlanMemoText } from "~/_legacy/types/analysis";
import type { CameraMemo } from "~/entities/camera-memo/types";
import type { Camera } from "~/entities/camera/types";
import { hasRealSnapshot, isCameraAnalyzed } from "~/entities/camera/utils";
import type { FloorPlan } from "~/entities/floor-plan/types";
import { DeleteConfirmDialog } from "~/pages/resource-management/DeleteConfirmDialog";
import { IcEraser } from "~/shared/assets/icons/IcEraser";
import { CameraObject } from "./fabric-objects/CameraObject";
import { pickCameraAt } from "./fabric-objects/camera-hit";
import {
  createFloorPlanMemoDiagramObject,
  createFloorPlanMemoTextObject,
  DEFAULT_DIAGRAM_MEMO_STYLE as DEFAULT_DIAGRAM_MEMO_STYLE_LOCAL,
  DEFAULT_TEXT_MEMO_STYLE as DEFAULT_TEXT_MEMO_STYLE_LOCAL,
  extractDiagramCoordinates,
  extractTextPosition,
  isFloorPlanMemoObject,
  type TaggedDiagram,
  type TaggedText,
} from "./fabric-objects/FloorPlanMemo";
import { MemoMarker } from "./fabric-objects/MemoMarker";
import { FloorPlanMemoEditPanel, type SelectedMemo } from "./FloorPlanMemoEditPanel";
import { useFloorPlanCanvas } from "./useFloorPlanCanvas";

const DEFAULT_BACKGROUND_IMAGE_WIDTH = 1500;

function resizeCameraIconScale(
  backgroundImageScaledWidth: number,
  iconSize: number,
): number {
  return (iconSize * backgroundImageScaledWidth) / DEFAULT_BACKGROUND_IMAGE_WIDTH;
}



interface FloorPlanCanvasProps {
  floorPlans: FloorPlan[];
  selectedFloorPlanId: number | null;
  onFloorPlanChange: (floorPlanId: number) => void;
  camerasOnFloorPlan: Camera[];
  /**
   * 선택된 카메라가 어디에도 매핑되지 않은 신규 카메라인지.
   * true 일 때만 도면 클릭이 placement 액션 — 다른 도면에 이미 배치된 카메라를
   * 선택한 채로 빈 영역을 클릭해도 placement conflict 모달이 뜨지 않게 함 (PO QA i 보강).
   */
  selectedCameraIsUnplaced?: boolean;
  cameraPositions: Map<number, { x: number; y: number; rotation: number; type: "default" | "add" | "edit" }>;
  selectedCameraId: number | null;
  onCameraMarkerClick: (camera: Camera) => void;
  onCameraPlacedOnFloorPlan: (
    cameraId: number,
    position: { x: number; y: number; rotation: number },
  ) => void;
  nonAnalysisNumberMap: Map<number, string>;
  iconScale: number;
  onIconScaleChange: (scale: number) => void;
  /** 드래그 확정 시점 — 저장은 여기서만 (onIconScaleChange 는 드래그 내내 발생) */
  onIconScaleCommit: (scale: number) => void;
  labelScale: number;
  onLabelScaleChange: (scale: number) => void;
  onLabelScaleCommit: (scale: number) => void;
  memosOnFloorPlan: CameraMemo[];
  memoPlacingMode: { memoId: number; cameraId: number } | null;
  onMemoPlaced: (memoId: number, position: { x: number; y: number }) => void;
  onMemoMarkerClick: (memoId: number | null) => void;
  onMemoDragEnd: (memoId: number, position: { x: number; y: number }) => void;
  selectedMemoId: number | null;
  onSelectionCleared: () => void;
  onDeleteCamera: (cameraId: number) => void;
  onDeleteMemoFromFloorPlan: (memoId: number) => void;
  floorPlanMemoDiagrams?: FloorPlanMemoDiagram[];
  floorPlanMemoTexts?: FloorPlanMemoText[];
  onMemoDiagramModified?: (
    memoId: number,
    floorPlanId: number,
    coordinates: { x: number; y: number }[],
    style: FloorPlanMemoDiagram["style"],
  ) => void;
  onMemoTextModified?: (
    memoId: number,
    floorPlanId: number,
    content: string,
    position: { x: number; y: number },
    style: FloorPlanMemoText["style"],
  ) => void;
  onMemoDiagramDelete?: (memoId: number, floorPlanId: number) => void;
  onMemoTextDelete?: (memoId: number, floorPlanId: number) => void;
  onMemoTextCreate?: (floorPlanId: number, position: { x: number; y: number }) => number;
  onMemoDiagramCreate?: (floorPlanId: number, coordinates: { x: number; y: number }[]) => number;
  onMemoTextStyleChange?: (memoId: number, partial: Partial<FloorPlanMemoText["style"]>) => void;
  onMemoDiagramStyleChange?: (memoId: number, partial: Partial<FloorPlanMemoDiagram["style"]>) => void;
  // 화각 조정 — 활성 카메라들의 조정 위치/회전 (Map). 메인 마커는 조정 위치에서 렌더, ghost 는 원본 위치
  adjustments?: Map<number, {
    adjustmentId: string;
    position: { x: number; y: number; rotation: number };
    frame: { dx: number; dy: number };
  }>;
  onAdjustmentMoved?: (cameraId: number, position: { x: number; y: number; rotation: number }) => void;
  // 조정 모드 진입 카메라 (한 번에 한 개). 메인 마커는 이 카메라일 때만 드래그 가능
  adjustmentEditingCameraId?: number | null;
  // 마커 툴바 액션
  onStartAdjustment?: (cameraId: number) => void;
  onEndAdjustment?: () => void;
  onAddMemo?: (cameraId: number) => void;
  /** PO QA 3차: 캔버스 위를 덮는 overlay(inline memo 등) 높이 — 도면 fit 가용 영역에서 제외 */
  topPadding?: number;
  /** PO QA 3차: 캔버스 하단 absolute action bar 높이 — fit 가용 영역에서 제외 */
  bottomPadding?: number;
}

/** 도면 메모 사각형 좌표가 같은지 — 되돌리기 등으로 상태가 바뀌었는지 판정용 */
function coordinatesMatch(
  a: { x: number; y: number }[],
  b: { x: number; y: number }[],
): boolean {
  if (a.length !== b.length) return false;
  return a.every((p, i) => Math.abs(p.x - b[i].x) < 0.01 && Math.abs(p.y - b[i].y) < 0.01);
}

export function FloorPlanCanvas({
  floorPlans,
  selectedFloorPlanId,
  onFloorPlanChange,
  camerasOnFloorPlan,
  selectedCameraIsUnplaced = false,
  cameraPositions,
  selectedCameraId,
  onCameraMarkerClick,
  onCameraPlacedOnFloorPlan,
  nonAnalysisNumberMap,
  iconScale,
  onIconScaleChange,
  onIconScaleCommit,
  labelScale,
  onLabelScaleChange,
  onLabelScaleCommit,
  memosOnFloorPlan,
  memoPlacingMode,
  onMemoPlaced,
  onMemoMarkerClick,
  onMemoDragEnd,
  selectedMemoId,
  onSelectionCleared,
  onDeleteCamera,
  onDeleteMemoFromFloorPlan,
  floorPlanMemoDiagrams,
  floorPlanMemoTexts,
  onMemoDiagramModified,
  onMemoTextModified,
  onMemoDiagramDelete,
  onMemoTextDelete,
  onMemoTextCreate,
  onMemoDiagramCreate,
  onMemoTextStyleChange,
  onMemoDiagramStyleChange,
  adjustments,
  onAdjustmentMoved,
  adjustmentEditingCameraId,
  onStartAdjustment,
  onEndAdjustment,
  onAddMemo,
  topPadding = 0,
  bottomPadding = 0,
}: FloorPlanCanvasProps) {
  const { t } = useTranslation("camera-mapping");
  const [deleteConfirm, setDeleteConfirm] = useState<
    | { type: "camera"; cameraId: number; name: string }
    | { type: "memo"; memoId: number }
    | { type: "floor-plan-memo"; memoKind: "text" | "diagram"; memoId: number; floorPlanId: number }
    | null
  >(null);
  const [showDeleteBlockedDialog, setShowDeleteBlockedDialog] = useState(false);
  // 토글 표시 여부 + 분석 상태만 React state, 위치는 ref+DOM 직접 갱신 (pan/drag 중 60fps)
  const [markerToolbar, setMarkerToolbar] = useState<{
    cameraId: number;
    isAnalyzed: boolean;
  } | null>(null);
  const markerToolbarRef = useRef<HTMLDivElement | null>(null);
  const [memoToolbar, setMemoToolbar] = useState<{ memoId: number } | null>(null);
  const memoToolbarRef = useRef<HTMLDivElement | null>(null);

  const selectedFloorPlan = floorPlans.find(
    (fp) => fp.id === selectedFloorPlanId,
  );

  const [memoCreateMode, setMemoCreateMode] = useState<"text" | "diagram" | null>(null);
  // 도면에 추가한 텍스트/사각형 일괄 표시/숨김 (개선 니즈 #8). 화면 표시만 바꾸며 데이터는 건드리지 않는다.
  const [showFloorPlanMemos, setShowFloorPlanMemos] = useState(true);
  const [fpSelectOpen, setFpSelectOpen] = useState(false);

  // placing 모드: 미배치 카메라가 선택된 상태 — 빈 영역 클릭이 placement 동작.
  // 컴포넌트 본문에서 SOT 로 계산하고 ① pan 비활성 ② placing cursor effect 양쪽에서 공유.
  // (이전: cursor effect 내부 인라인 계산 → pan 비활성 조건과 불일치 → useFloorPlanCanvas
  //  pan 핸들러가 cameraCursor 를 캡쳐/복원해 placement 후 cursor 잔존하던 버그. PO QA 2차 i 본질 원인)
  const inPlacingMode = useMemo(() => {
    if (selectedMemoId != null || selectedCameraId == null) return false;
    if (!selectedCameraIsUnplaced) return false;
    return !camerasOnFloorPlan.some((c) => c.id === selectedCameraId);
  }, [selectedMemoId, selectedCameraId, selectedCameraIsUnplaced, camerasOnFloorPlan]);

  // 이벤트 핸들러에서 stale closure 없이 접근할 수 있도록 ref 동기화 (PO QA 7차 bb)
  const inPlacingModeRef = useRef(false);
  inPlacingModeRef.current = inPlacingMode;

  // memoCreateMode 활성 시 좌클릭 패닝 비활성화 (메모 생성 클릭/드래그 우선).
  // inPlacingMode / memoPlacingMode 일 때도 비활성 — 빈 영역 left-click 은 오직 placement 여야 하고,
  // pan 핸들러가 동시에 트리거되면 mouse:up 에서 captured placing cursor 가 복원되어 cursor 잔존 (PO QA 3차 w).
  const panningEnabledRefForCanvas = useRef(true);
  panningEnabledRefForCanvas.current = memoCreateMode === null && !inPlacingMode && memoPlacingMode === null;

  const {
    fabricRef,
    canvasRef,
    containerRef,
    isReady,
    zoomIn: handleZoomIn,
    zoomOut: handleZoomOut,
    percentToPixel,
    pixelToPercent,
    getBackgroundScaledWidth,
  } = useFloorPlanCanvas({
    imageUrl: selectedFloorPlan?.imageUrl ?? null,
    panningEnabledRef: panningEnabledRefForCanvas,
    topPadding,
    bottomPadding,
    verticalAlign: "center",
  });

  // 안정적 ref로 콜백 접근 (이벤트 핸들러에서 stale closure 방지)
  const callbacksRef = useRef({
    onCameraMarkerClick,
    onCameraPlacedOnFloorPlan,
    onMemoMarkerClick,
    onMemoPlaced,
    onMemoDragEnd,
    onSelectionCleared,
  });
  callbacksRef.current = {
    onCameraMarkerClick,
    onCameraPlacedOnFloorPlan,
    onMemoMarkerClick,
    onMemoPlaced,
    onMemoDragEnd,
    onSelectionCleared,
  };

  const memoPlacingModeRef = useRef(memoPlacingMode);
  memoPlacingModeRef.current = memoPlacingMode;

  const selectedCameraIdRef = useRef(selectedCameraId);
  selectedCameraIdRef.current = selectedCameraId;

  const selectedMemoIdRef = useRef(selectedMemoId);
  selectedMemoIdRef.current = selectedMemoId;

  const camerasOnFloorPlanRef = useRef(camerasOnFloorPlan);
  camerasOnFloorPlanRef.current = camerasOnFloorPlan;

  const selectedCameraIsUnplacedRef = useRef(selectedCameraIsUnplaced);
  selectedCameraIsUnplacedRef.current = selectedCameraIsUnplaced;

  const adjustmentsRef = useRef(adjustments);
  adjustmentsRef.current = adjustments;
  const onAdjustmentMovedRef = useRef(onAdjustmentMoved);
  onAdjustmentMovedRef.current = onAdjustmentMoved;
  const adjustmentEditingCameraIdRef = useRef(adjustmentEditingCameraId ?? null);
  adjustmentEditingCameraIdRef.current = adjustmentEditingCameraId ?? null;

  // 프로그래밍적 선택(포커스 동기화)과 사용자 클릭을 구분하는 플래그
  const isProgrammaticSelectionRef = useRef(false);

  const [showIconScalePopover, setShowIconScalePopover] = useState(false);
  const [selectedMemo, setSelectedMemo] = useState<SelectedMemo | null>(null);
  // 팝오버 외부 클릭 닫기는 CameraScalePopover 내부에서 처리

  // (RV4) 도면 셀렉트 드롭다운 외부 클릭 / Esc 로 닫기 (radix→커스텀 버튼 전환 시 누락됐던 동작 복원)
  const fpSelectRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!fpSelectOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (fpSelectRef.current && !fpSelectRef.current.contains(e.target as Node)) {
        setFpSelectOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFpSelectOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [fpSelectOpen]);

  // --- 카메라 shapes 동기화 (위치/추가/제거/아이콘 크기만, 포커스 제외) ---
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isReady) return;

    // 도면 전환 직후 canvas.clear()가 배경을 제거했지만 setIsReady(false)가
    // 아직 반영 안 된 렌더 사이클 — 배경 없이 카메라를 배치하면 (0,0)에 몰림
    const hasBg = canvas.getObjects().some((o) => (o as any).__isFloorPlanBg);
    if (!hasBg) return;

    const bgScaledWidth = getBackgroundScaledWidth();
    const resizedScale = resizeCameraIconScale(bgScaledWidth, iconScale);

    const existingCameraObjects = canvas
      .getObjects()
      .filter((o): o is CameraObject => o instanceof CameraObject);
    const existingMainObjects = existingCameraObjects.filter((o) => !o.isGhost);
    const existingGhostObjects = existingCameraObjects.filter((o) => o.isGhost);

    const toAdd: CameraObject[] = [];
    const toRemove: CameraObject[] = [];
    let changed = false;

    // 도면에서 제거된 카메라 제거 (ghost 포함)
    existingCameraObjects.forEach((obj) => {
      if (!camerasOnFloorPlan.some((c) => c.id === obj.cameraId)) {
        toRemove.push(obj);
      }
    });

    camerasOnFloorPlan.forEach((camera) => {
      const matched = existingMainObjects.find(
        (obj) => obj.cameraId === camera.id,
      );
      const matchedGhost = existingGhostObjects.find(
        (obj) => obj.cameraId === camera.id,
      );

      const localInfo = cameraPositions.get(camera.id);
      const adjustment = adjustments?.get(camera.id) ?? null;
      const hasAdjustment = adjustment != null;
      const serverPosition = camera.position
        ? { x: camera.position.x, y: camera.position.y }
        : null;
      // 조정 활성: 메인 마커는 조정 위치, ghost 는 원본(camera.position)
      const adjustedPosition = adjustment
        ? { x: adjustment.position.x, y: adjustment.position.y }
        : null;
      // 화각 조정 활성 시 조정 위치가 localMappings(원본 매핑) 보다 우선 — 그렇지 않으면 드래그 후 원본 위치로 되돌아감
      const mainPosition = hasAdjustment && adjustedPosition
        ? adjustedPosition
        : localInfo ?? serverPosition ?? getTempPosition(camera, camerasOnFloorPlan);
      const mainRotation = hasAdjustment && adjustment
        ? adjustment.position.rotation
        : localInfo?.rotation ?? camera.position?.rotation ?? 0;
      const displayNumber = camera.analysisNumber ?? nonAnalysisNumberMap.get(camera.id) ?? null;

      const effectiveMappingType = localInfo?.type ?? camera.mappingType;
      // 화각 조정 라벨/스타일은 effectiveAdjustment(서버+로컬) 기준 — server mappingType 만으로 결정 안 함
      // (mappingType="edit" 인데 adjustmentPosition 데이터가 비어있던 과거 데이터 일관성 문제 방지)
      const cameraType = hasAdjustment
        ? "edit"
        : effectiveMappingType === "add"
          ? "add"
          : "default";
      const cameraIdPrefix =
        cameraType === "add"
          ? t("card.added")
          : cameraType === "edit"
            ? t("card.adjusted")
            : null;

      const px = percentToPixel(mainPosition);
      const isFocused = camera.id === selectedCameraIdRef.current;

      if (!matched) {
        const cameraObj = new CameraObject({
          cameraId: camera.id,
          cameraType,
          displayNumber,
          cameraIdPrefix,
          left: px.x,
          top: px.y,
          angle: mainRotation,
          iconScale: resizedScale,
          labelFontScale: labelScale,
          isFocused,
        });
        toAdd.push(cameraObj);
      } else {
        // 위치/스케일/속성 변경 여부 체크 후 업데이트
        const posChanged =
          Math.abs(matched.left! - px.x) > 0.01 ||
          Math.abs(matched.top! - px.y) > 0.01 ||
          Math.abs(matched.angle - mainRotation) > 0.01;
        const typeChanged = matched.cameraType !== cameraType;
        const scaleChanged = Math.abs(matched.iconScale - resizedScale) > 0.001;
        const labelChanged =
          matched.displayNumber !== displayNumber ||
          matched.cameraIdPrefix !== cameraIdPrefix ||
          Math.abs(matched.labelFontScale - labelScale) > 0.001;

        if (posChanged || typeChanged || scaleChanged || labelChanged) {
          if (posChanged) {
            matched.set({ left: px.x, top: px.y, angle: mainRotation });
          }
          matched.updateAppearance({
            iconScale: resizedScale,
            labelFontScale: labelScale,
            displayNumber,
            cameraIdPrefix,
            cameraType,
          });
          matched.setCoords();
          changed = true;
        }
      }

      // Ghost 마커 동기화 — adjustment 가 있을 때만 원본 위치를 표시.
      // PO QA 4차 ae: ghost 는 "사용자가 인지하는 현재 base" 를 보여야 함 — localMappings(미저장 drag) 가 있으면 그 위치, 없으면 camera.position(서버).
      // 미저장 drag 후 화각 조정 시작 시 ghost 가 서버 위치로 튀어서 "기본화각 이동" 으로 보이던 문제 해결.
      const ghostBase = localInfo ?? serverPosition;
      if (hasAdjustment && ghostBase) {
        const ghostPx = percentToPixel(ghostBase);
        const ghostRotation = localInfo?.rotation ?? camera.position?.rotation ?? 0;
        if (!matchedGhost) {
          const ghostObj = new CameraObject({
            cameraId: camera.id,
            // ghost 라벨도 주황(조정 전)으로 — 외곽선/연결선과 톤 통일
            cameraType: "edit",
            displayNumber,
            cameraIdPrefix: null,
            left: ghostPx.x,
            top: ghostPx.y,
            angle: ghostRotation,
            iconScale: resizedScale,
            labelFontScale: labelScale,
            isFocused: false,
            isGhost: true,
          });
          toAdd.push(ghostObj);
        } else {
          const ghostPosChanged =
            Math.abs(matchedGhost.left! - ghostPx.x) > 0.01 ||
            Math.abs(matchedGhost.top! - ghostPx.y) > 0.01 ||
            Math.abs(matchedGhost.angle - ghostRotation) > 0.01;
          const ghostScaleChanged = Math.abs(matchedGhost.iconScale - resizedScale) > 0.001;
          const ghostLabelChanged =
            matchedGhost.displayNumber !== displayNumber ||
            Math.abs(matchedGhost.labelFontScale - labelScale) > 0.001;
          if (ghostPosChanged || ghostScaleChanged || ghostLabelChanged) {
            if (ghostPosChanged) {
              matchedGhost.set({ left: ghostPx.x, top: ghostPx.y, angle: ghostRotation });
            }
            matchedGhost.updateAppearance({
              iconScale: resizedScale,
              labelFontScale: labelScale,
              displayNumber,
            });
            matchedGhost.setCoords();
            changed = true;
          }
        }
      } else if (matchedGhost) {
        // adjustment 가 사라지면 ghost 도 제거
        toRemove.push(matchedGhost);
      }
    });

    if (toRemove.length > 0) {
      toRemove.forEach((obj) => canvas.remove(obj));
      changed = true;
    }
    if (toAdd.length > 0) {
      toAdd.forEach((obj) => {
        canvas.add(obj);
        // ghost(조정 전 외곽선)는 조정 후 마커 위로 — 각도만 회전해 겹칠 때 뒤에 가려지지 않게
        if (obj instanceof CameraObject && obj.isGhost) canvas.bringObjectToFront(obj);
      });
      changed = true;
    }

    // --- 화각 조정 연결선 (ghost 원본 ↔ main 조정본) ---
    // 선택된 카메라일 때만 노출. ghost 와 톤 맞춤 (주황 반투명)
    const existingLines = canvas
      .getObjects()
      .filter((o): o is Line & { __adjustmentCameraId?: number } =>
        o instanceof Line && (o as any).__adjustmentCameraId != null,
      );
    const linesToRemove: Line[] = [];
    const handledLineCameraIds = new Set<number>();
    camerasOnFloorPlan.forEach((camera) => {
      const adjustment = adjustments?.get(camera.id);
      // PO QA 4차 ae: ghost 와 연결선 시작점을 동일하게 — localMappings(미저장 drag) 가 있으면 그 위치, 없으면 camera.position
      const lineLocalInfo = cameraPositions.get(camera.id);
      const lineGhostBase = lineLocalInfo
        ? { x: lineLocalInfo.x, y: lineLocalInfo.y }
        : (camera.position ? { x: camera.position.x, y: camera.position.y } : null);
      const isSelected = camera.id === selectedCameraIdRef.current;
      if (!adjustment || !lineGhostBase || !isSelected) return;
      const ghostPx = percentToPixel(lineGhostBase);
      const mainPx = percentToPixel({ x: adjustment.position.x, y: adjustment.position.y });
      const matchedLine = existingLines.find(
        (l) => (l as any).__adjustmentCameraId === camera.id,
      );
      if (!matchedLine) {
        const line = new Line([ghostPx.x, ghostPx.y, mainPx.x, mainPx.y], {
          stroke: "rgba(240, 133, 28, 0.6)", // COLOR_GHOST(#F0851C) — ghost 앰버 톤과 맞춤
          strokeWidth: 1.5,
          strokeDashArray: [6, 4],
          selectable: false,
          evented: false,
          hoverCursor: "default",
          objectCaching: false,
        });
        (line as any).__adjustmentCameraId = camera.id;
        canvas.add(line);
        canvas.sendObjectToBack(line);
        // floor plan 이미지(index 0) 위, ghost 와 같은 레이어로
        canvas.bringObjectForward(line);
        changed = true;
      } else {
        const sameStart =
          Math.abs((matchedLine.x1 ?? 0) - ghostPx.x) < 0.01
          && Math.abs((matchedLine.y1 ?? 0) - ghostPx.y) < 0.01;
        const sameEnd =
          Math.abs((matchedLine.x2 ?? 0) - mainPx.x) < 0.01
          && Math.abs((matchedLine.y2 ?? 0) - mainPx.y) < 0.01;
        if (!sameStart || !sameEnd) {
          matchedLine.set({ x1: ghostPx.x, y1: ghostPx.y, x2: mainPx.x, y2: mainPx.y });
          matchedLine.setCoords();
          changed = true;
        }
      }
      handledLineCameraIds.add(camera.id);
    });
    existingLines.forEach((line) => {
      const id = (line as any).__adjustmentCameraId;
      if (id == null || !handledLineCameraIds.has(id)) {
        linesToRemove.push(line);
      }
    });
    if (linesToRemove.length > 0) {
      linesToRemove.forEach((line) => canvas.remove(line));
      changed = true;
    }

    // ghost 마커를 floor plan 이미지 위, 다른 모든 객체 아래에 배치 (z-order)
    canvas.getObjects().forEach((obj) => {
      if (obj instanceof CameraObject && obj.isGhost) {
        canvas.sendObjectToBack(obj);
        // floor plan 이미지(index 0) 위로 한 단계 올림
        canvas.bringObjectForward(obj);
      }
    });
    if (changed) {
      canvas.requestRenderAll();
    }
  }, [
    isReady,
    camerasOnFloorPlan,
    cameraPositions,
    nonAnalysisNumberMap,
    iconScale,
    fabricRef,
    percentToPixel,
    getBackgroundScaledWidth,
    t,
    labelScale,
    adjustments,
    adjustmentEditingCameraId,
    selectedCameraId,
  ]);

  // --- 화각 조정 연결선 실시간 추종: object:moving 마다 매칭되는 line 의 x2/y2 갱신 ---
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isReady) return;

    const onMoving = (opt: any) => {
      const target = opt.target;
      if (!(target instanceof CameraObject) || target.isGhost) return;
      const line = canvas
        .getObjects()
        .find(
          (o): o is Line =>
            o instanceof Line && (o as any).__adjustmentCameraId === target.cameraId,
        );
      if (!line) return;
      line.set({ x2: target.left, y2: target.top });
      line.setCoords();
    };

    canvas.on("object:moving", onMoving);
    return () => {
      canvas.off("object:moving", onMoving);
    };
  }, [fabricRef, isReady]);

  // --- 메모 shapes 동기화 ---
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isReady) return;

    // PO QA 3차: 도면 전환 직후 canvas.clear() 이후 setIsReady(false) 가 아직 반영 안 된 사이클 가드 — bg 없이 메모 마커 추가하면 (0,0) 에 몰림
    const hasBg = canvas.getObjects().some((o) => (o as any).__isFloorPlanBg);
    if (!hasBg) return;

    // PO QA 2차 l ②: 도면 위 메모 아이콘은 해당 카메라 선택 시에만 노출 (레거시 동일).
    const visibleMemos = selectedCameraId == null
      ? []
      : memosOnFloorPlan.filter((m) => m.cameraId === selectedCameraId);

    const existingMemoMarkers = canvas
      .getObjects()
      .filter((o): o is MemoMarker => o instanceof MemoMarker);

    const toAdd: MemoMarker[] = [];
    const toRemove: MemoMarker[] = [];
    let memoChanged = false;

    existingMemoMarkers.forEach((obj) => {
      if (!visibleMemos.some((m) => m.id === obj.memoId)) {
        toRemove.push(obj);
      }
    });

    visibleMemos.forEach((memo) => {
      if (!memo.positionForFloorPlan) return;

      const matched = existingMemoMarkers.find(
        (obj) => obj.memoId === memo.id,
      );

      const px = percentToPixel(memo.positionForFloorPlan);

      if (!matched) {
        const marker = new MemoMarker({
          memoId: memo.id,
          cameraId: memo.cameraId,
          left: px.x,
          top: px.y,
        });
        toAdd.push(marker);
      } else {
        const posChanged =
          Math.abs(matched.left! - px.x) > 0.01 ||
          Math.abs(matched.top! - px.y) > 0.01;
        if (posChanged) {
          matched.set({ left: px.x, top: px.y });
          matched.setCoords();
          memoChanged = true;
        }
      }
    });

    if (toRemove.length > 0) {
      toRemove.forEach((obj) => canvas.remove(obj));
      memoChanged = true;
    }
    if (toAdd.length > 0) {
      toAdd.forEach((obj) => canvas.add(obj));
      memoChanged = true;
    }
    if (memoChanged) {
      canvas.requestRenderAll();
    }
  }, [isReady, memosOnFloorPlan, selectedCameraId, fabricRef, percentToPixel]);

  // --- 도면 메모(다이어그램·텍스트) 렌더링 (diff 기반) ---
  // 매 변경 시 모두 제거하지 않고 추가/제거/업데이트만 처리해 race condition 회피.
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isReady) return;

    // PO QA 3차: 도면 전환 직후 bg 가 아직 없는 사이클 가드 — 메모 객체가 (0,0) 에 몰림 방지
    const hasBg = canvas.getObjects().some((o) => (o as any).__isFloorPlanBg);
    if (!hasBg) return;

    const ctx = { percentToPixel, pixelToPercent };
    const editable = true;

    const existing = canvas.getObjects().filter(isFloorPlanMemoObject);
    const wantedDiagramIds = new Set((floorPlanMemoDiagrams ?? []).map((d) => d.id));
    const wantedTextIds = new Set((floorPlanMemoTexts ?? []).map((t) => t.id));

    let changed = false;

    // 1) 사라진 객체 제거 — wantedIds 에 없는 객체는 캔버스에서 제거
    //    (임시 ID도 pending이 비워지면 wantedIds에서 사라지므로 정상 제거됨)
    for (const obj of existing) {
      const wanted = obj.__memoKind === "diagram"
        ? wantedDiagramIds.has(obj.__memoId)
        : wantedTextIds.has(obj.__memoId);
      if (!wanted) {
        canvas.remove(obj as never);
        changed = true;
      }
    }

    // 2) 추가/업데이트
    const stillExisting = canvas.getObjects().filter(isFloorPlanMemoObject);
    (floorPlanMemoDiagrams ?? []).forEach((memo) => {
      const found = stillExisting.find((o) => o.__memoKind === "diagram" && o.__memoId === memo.id);
      if (!found) {
        canvas.add(createFloorPlanMemoDiagramObject(memo, ctx, { editable }));
        changed = true;
        return;
      }
      // 상태가 바뀌면(되돌리기 등) 기존 객체도 다시 그린다. 예전엔 "편집 중일 수 있다"는 이유로
      // 통째로 생략해서, Ctrl+Z 로 좌표가 되돌아가도 화면의 도형은 그대로 남았다 (개선 니즈 #3).
      // 사용자가 지금 잡고 있는 객체(활성)만 예외로 둬서 드래그 중 튀는 것을 막는다.
      // 카메라 마커가 쓰는 방식과 같다.
      if (canvas.getActiveObject() === found) return;
      const currentCoords = extractDiagramCoordinates(found as TaggedDiagram, pixelToPercent);
      if (!coordinatesMatch(currentCoords, memo.coordinates)) {
        canvas.remove(found as never);
        canvas.add(createFloorPlanMemoDiagramObject(memo, ctx, { editable }));
        changed = true;
      }
    });
    (floorPlanMemoTexts ?? []).forEach((memo) => {
      const found = stillExisting.find((o) => o.__memoKind === "text" && o.__memoId === memo.id);
      if (!found) {
        canvas.add(createFloorPlanMemoTextObject(memo, ctx, { editable }));
        changed = true;
        return;
      }
      if (canvas.getActiveObject() === found) return;
      const text = found as TaggedText;
      const wanted = percentToPixel(memo.position);
      const positionChanged =
        Math.abs((text.left ?? 0) - wanted.x) > 0.01
        || Math.abs((text.top ?? 0) - wanted.y) > 0.01;
      if (positionChanged || text.text !== memo.content) {
        canvas.remove(found as never);
        canvas.add(createFloorPlanMemoTextObject(memo, ctx, { editable }));
        changed = true;
      }
    });

    if (changed) {
      // 도면 메모는 카메라/메모 마커 아래 + 배경 위
      canvas.getObjects().forEach((obj) => {
        if (isFloorPlanMemoObject(obj)) canvas.sendObjectToBack(obj);
      });
      const bg = canvas.getObjects().find((o) => (o as { __isFloorPlanBg?: boolean }).__isFloorPlanBg);
      if (bg) canvas.sendObjectToBack(bg);
      canvas.requestRenderAll();
    }
  }, [isReady, floorPlanMemoDiagrams, floorPlanMemoTexts, fabricRef, percentToPixel, pixelToPercent]);

  // 텍스트/사각형 일괄 표시/숨김 (개선 니즈 #8)
  // 숨길 때 선택도 해제한다. 보이지 않는 객체가 선택된 채 남으면 Backspace 로 지워질 수 있다.
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isReady) return;

    let changed = false;
    for (const obj of canvas.getObjects()) {
      if (!isFloorPlanMemoObject(obj)) continue;
      if (obj.visible === showFloorPlanMemos) continue;
      obj.set({
        visible: showFloorPlanMemos,
        evented: showFloorPlanMemos,
        selectable: showFloorPlanMemos,
      });
      changed = true;
    }
    if (!changed) return;

    const active = canvas.getActiveObject();
    if (!showFloorPlanMemos && active && isFloorPlanMemoObject(active)) {
      canvas.discardActiveObject();
    }
    canvas.requestRenderAll();
  }, [showFloorPlanMemos, isReady, fabricRef, floorPlanMemoDiagrams, floorPlanMemoTexts]);

  // --- 도면 메모 편집 이벤트 (이동 / 텍스트 수정 → mutation) ---
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isReady) return;

    const handleModified = (e: { target?: unknown }) => {
      const target = e.target;
      if (!target || !isFloorPlanMemoObject(target)) return;

      if (target.__memoKind === "diagram") {
        const diagram = target as TaggedDiagram;
        // 좌표 추출은 변환 행렬(회전+스케일) 반영 상태에서 수행
        const coords = extractDiagramCoordinates(diagram, pixelToPercent);
        // fabric은 scale로 크기 변화를 표현 — width/height에 흡수 (angle은 유지)
        // getScaledWidth()는 strokeUniform 시 strokeWidth를 더하므로 직접 계산
        const newWidth = (diagram.width ?? 0) * Math.abs(diagram.scaleX ?? 1);
        const newHeight = (diagram.height ?? 0) * Math.abs(diagram.scaleY ?? 1);
        diagram.set({ width: newWidth, height: newHeight, scaleX: 1, scaleY: 1 });
        diagram.setCoords();
        onMemoDiagramModified?.(
          diagram.__memoId,
          diagram.__floorPlanId,
          coords,
          diagram.__style,
        );
      } else if (target.__memoKind === "text") {
        const text = target as TaggedText;
        // scaleX로 들어간 변경을 width로 흡수 (글자 폭이 안 늘어나고 텍스트만 wrap되도록)
        const scaledWidth = (text.width ?? 0) * Math.abs(text.scaleX ?? 1);
        text.set({ width: scaledWidth, scaleX: 1, scaleY: 1 });
        text.setCoords();
        const position = extractTextPosition(text, pixelToPercent);
        onMemoTextModified?.(
          text.__memoId,
          text.__floorPlanId,
          text.text,
          position,
          { ...text.__style, width: scaledWidth, rotation: text.angle ?? 0 },
        );
      }
    };

    canvas.on("object:modified", handleModified);
    return () => {
      canvas.off("object:modified", handleModified);
    };
  }, [isReady, fabricRef, pixelToPercent, onMemoDiagramModified, onMemoTextModified]);

  // --- 도면 메모 신규 생성: 마우스 클릭(텍스트) / 드래그(사각형 도형) ---
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isReady || !memoCreateMode || selectedFloorPlanId == null) return;

    canvas.defaultCursor = "crosshair";
    canvas.hoverCursor = "crosshair";
    // 모드 활성 동안 일반 객체 선택 비활성
    canvas.selection = false;

    let dragStart: { x: number; y: number } | null = null;
    let dragRect: Rect | null = null;

    const handleDown = (opt: TPointerEventInfo<TPointerEvent>) => {
      const ptr = { x: opt.scenePoint.x, y: opt.scenePoint.y };
      if (memoCreateMode === "text") {
        const pos = pixelToPercent({ x: ptr.x, y: ptr.y });
        const tempId = onMemoTextCreate?.(selectedFloorPlanId, pos);
        // fabric에 즉시 추가 — useEffect/state 사이클에 의존하지 않고 보이게
        if (tempId != null) {
          const obj = createFloorPlanMemoTextObject(
            { id: tempId, floorPlanId: selectedFloorPlanId, content: t("floorPlanMemo.defaultTextContent", { ns: "shared" }), position: pos, style: DEFAULT_TEXT_MEMO_STYLE_LOCAL },
            { percentToPixel, pixelToPercent },
            { editable: true },
          );
          canvas.add(obj);
          // 새로 만든 메모를 활성 객체로 — Delete 키가 카메라 삭제로 fallback 되지 않도록 (j)
          canvas.setActiveObject(obj);
          canvas.requestRenderAll();
        }
        setMemoCreateMode(null);
      } else if (memoCreateMode === "diagram") {
        dragStart = { x: ptr.x, y: ptr.y };
      }
    };

    const handleMove = (opt: TPointerEventInfo<TPointerEvent>) => {
      if (memoCreateMode !== "diagram" || !dragStart) return;
      const ptr = { x: opt.scenePoint.x, y: opt.scenePoint.y };
      // 임시 사각형 미리보기
      const left = Math.min(dragStart.x, ptr.x);
      const top = Math.min(dragStart.y, ptr.y);
      const width = Math.abs(ptr.x - dragStart.x);
      const height = Math.abs(ptr.y - dragStart.y);
      if (!dragRect) {
        dragRect = new Rect({
          left,
          top,
          width,
          height,
          originX: "left",
          originY: "top",
          fill: "rgba(72, 75, 210, 0.12)",
          stroke: "#484BD2",
          strokeWidth: 2,
          selectable: false,
          evented: false,
          objectCaching: false,
        });
        canvas.add(dragRect);
      } else {
        dragRect.set({ left, top, width, height });
      }
      canvas.requestRenderAll();
    };

    const handleUp = () => {
      if (memoCreateMode !== "diagram" || !dragStart) return;
      // dragRect 미리보기를 그대로 메모 객체로 변환 (위치 정확히 일치)
      if (dragRect && (dragRect.width ?? 0) >= 4 && (dragRect.height ?? 0) >= 4) {
        const left = dragRect.left ?? 0;
        const top = dragRect.top ?? 0;
        const width = dragRect.width ?? 0;
        const height = dragRect.height ?? 0;
        const pts = [
          pixelToPercent({ x: left, y: top }),
          pixelToPercent({ x: left + width, y: top }),
          pixelToPercent({ x: left + width, y: top + height }),
          pixelToPercent({ x: left, y: top + height }),
        ];
        const tempId = onMemoDiagramCreate?.(selectedFloorPlanId, pts);
        if (tempId != null) {
          // dragRect를 그대로 메모 객체로 promote — 좌표 round-trip 없음 → 픽셀 단위 일치
          dragRect.set({
            fill: DEFAULT_DIAGRAM_MEMO_STYLE_LOCAL.fill ?? "transparent",
            stroke: DEFAULT_DIAGRAM_MEMO_STYLE_LOCAL.borderColor,
            strokeWidth: DEFAULT_DIAGRAM_MEMO_STYLE_LOCAL.borderColor ? 3 : 0,
            strokeUniform: true,
            opacity: DEFAULT_DIAGRAM_MEMO_STYLE_LOCAL.opacity,
            selectable: true,
            evented: true,
            hasControls: true,
            hasBorders: true,
            lockScalingX: false,
            lockScalingY: false,
            lockRotation: false,
            transparentCorners: false,
            cornerSize: 8,
            cornerColor: "#484BD2",
            cornerStyle: "circle",
          });
          (dragRect as unknown as TaggedDiagram).__memoKind = "diagram";
          (dragRect as unknown as TaggedDiagram).__memoId = tempId;
          (dragRect as unknown as TaggedDiagram).__floorPlanId = selectedFloorPlanId;
          (dragRect as unknown as TaggedDiagram).__style = DEFAULT_DIAGRAM_MEMO_STYLE_LOCAL;
          dragRect.setCoords();
          // 새로 만든 메모를 활성 객체로 — Delete 키가 카메라 삭제로 fallback 되지 않도록 (j)
          canvas.setActiveObject(dragRect);
          dragRect = null; // 더 이상 미리보기가 아님 — cleanup에서 제거 안 됨
          canvas.requestRenderAll();
        }
      }
      // 4px 미만이거나 promote 못 한 dragRect는 제거
      if (dragRect) {
        canvas.remove(dragRect);
        dragRect = null;
      }
      dragStart = null;
      setMemoCreateMode(null);
    };

    canvas.on("mouse:down", handleDown);
    canvas.on("mouse:move", handleMove);
    canvas.on("mouse:up", handleUp);

    return () => {
      canvas.off("mouse:down", handleDown);
      canvas.off("mouse:move", handleMove);
      canvas.off("mouse:up", handleUp);
      if (dragRect) {
        canvas.remove(dragRect);
      }
      // memo create mode 종료 시 기본 상태 — pan 가능 표시 grab
      canvas.defaultCursor = "grab";
      canvas.hoverCursor = "move";
      canvas.selection = true;
    };
  }, [memoCreateMode, isReady, fabricRef, pixelToPercent, selectedFloorPlanId, onMemoTextCreate, onMemoDiagramCreate]);

  // --- 도면 요소(텍스트/사각형) 삭제: Delete/Backspace 키 ---
  // 단순 오타·실수로 손실되는 것을 막기 위해 확인 다이얼로그를 띄움 (PO QA 2차 n)
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isReady) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      // input/textarea/contenteditable에 포커스 있으면 무시 (속성 패널 입력 등)
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable) return;
      }
      const active = canvas.getActiveObject();
      if (!active || !isFloorPlanMemoObject(active)) return;
      // 텍스트 편집 모드에선 일반 삭제 키 동작 보존
      if (active.__memoKind === "text" && (active as { isEditing?: boolean }).isEditing) return;

      e.preventDefault();
      setDeleteConfirm({
        type: "floor-plan-memo",
        memoKind: active.__memoKind,
        memoId: active.__memoId,
        floorPlanId: active.__floorPlanId,
      });
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isReady, fabricRef]);

  // --- 도면 메모 선택 추적 (편집 패널용) ---
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isReady) return;

    const sync = () => {
      const active = canvas.getActiveObject();
      if (active && isFloorPlanMemoObject(active)) {
        if (active.__memoKind === "text") {
          const t = active as TaggedText;
          setSelectedMemo({
            kind: "text",
            id: t.__memoId,
            floorPlanId: t.__floorPlanId,
            content: t.text,
            style: t.__style,
          });
        } else {
          const d = active as TaggedDiagram;
          setSelectedMemo({
            kind: "diagram",
            id: d.__memoId,
            floorPlanId: d.__floorPlanId,
            style: d.__style,
          });
        }
      } else {
        setSelectedMemo(null);
      }
    };
    canvas.on("selection:created", sync);
    canvas.on("selection:updated", sync);
    canvas.on("selection:cleared", sync);
    return () => {
      canvas.off("selection:created", sync);
      canvas.off("selection:updated", sync);
      canvas.off("selection:cleared", sync);
    };
  }, [isReady, fabricRef]);

  // 패널의 속성 변경을 fabric 객체에 즉시 반영하고 부모 콜백으로 전달
  const handleTextStyleChange = useCallback(
    (memoId: number, partial: Partial<FloorPlanMemoText["style"]>) => {
      const canvas = fabricRef.current;
      if (!canvas) return;
      const target = canvas
        .getObjects()
        .find((o): o is TaggedText => isFloorPlanMemoObject(o) && o.__memoKind === "text" && o.__memoId === memoId);
      if (!target) return;
      const newStyle: FloorPlanMemoText["style"] = { ...target.__style, ...partial };
      target.__style = newStyle;
      target.set({
        fontSize: newStyle.fontSize,
        fill: newStyle.color,
        opacity: newStyle.opacity,
        width: newStyle.width,
        stroke: newStyle.hasStroke ? "white" : undefined,
        strokeWidth: newStyle.hasStroke ? 2 : 0,
      });
      target.setCoords();
      canvas.requestRenderAll();
      setSelectedMemo((prev) =>
        prev && prev.kind === "text" && prev.id === memoId
          ? { ...prev, style: newStyle }
          : prev,
      );
      onMemoTextStyleChange?.(memoId, partial);
    },
    [fabricRef, onMemoTextStyleChange],
  );

  const handleDiagramStyleChange = useCallback(
    (memoId: number, partial: Partial<FloorPlanMemoDiagram["style"]>) => {
      const canvas = fabricRef.current;
      if (!canvas) return;
      const target = canvas
        .getObjects()
        .find((o): o is TaggedDiagram => isFloorPlanMemoObject(o) && o.__memoKind === "diagram" && o.__memoId === memoId);
      if (!target) return;
      const newStyle: FloorPlanMemoDiagram["style"] = { ...target.__style, ...partial };
      target.__style = newStyle;
      target.set({
        fill: newStyle.fill ?? "transparent",
        stroke: newStyle.borderColor,
        strokeWidth: newStyle.borderColor ? 3 : 0,
        opacity: newStyle.opacity,
      });
      target.setCoords();
      canvas.requestRenderAll();
      setSelectedMemo((prev) =>
        prev && prev.kind === "diagram" && prev.id === memoId
          ? { ...prev, style: newStyle }
          : prev,
      );
      onMemoDiagramStyleChange?.(memoId, partial);
    },
    [fabricRef, onMemoDiagramStyleChange],
  );

  // --- 선택 이벤트: 카메라/메모 클릭 ---
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    const syncMemoFocus = (activeObj: any) => {
      canvas.getObjects().forEach((obj) => {
        if (obj instanceof MemoMarker) {
          const shouldFocus = obj === activeObj;
          if (obj.isFocused !== shouldFocus) {
            obj.updateAppearance({ isFocused: shouldFocus });
          }
        }
      });
    };

    const handleSelection = (e: any) => {
      // 프로그래밍적 선택(포커스 동기화)은 무시 — 사용자 클릭만 처리
      if (isProgrammaticSelectionRef.current) return;
      // 배치 모드 중 기존 마커 선택 이벤트 무시 — 배치 우선 (PO QA 7차 bb)
      if (inPlacingModeRef.current) return;

      const target = e.selected?.[0];
      if (!target) return;

      syncMemoFocus(target);

      if (target instanceof CameraObject) {
        const camera = camerasOnFloorPlanRef.current.find(
          (c) => c.id === target.cameraId,
        );
        if (camera) callbacksRef.current.onCameraMarkerClick(camera);
      } else if (target instanceof MemoMarker) {
        callbacksRef.current.onMemoMarkerClick(target.memoId);
      }
    };

    const handleSelectionCleared = () => {
      if (isProgrammaticSelectionRef.current) return;
      // 캔버스 빈 공간 클릭만으로는 카메라/메모 포커스 해제하지 않음 — 다른 항목 선택 시에만 변경
    };

    canvas.on("selection:created", handleSelection);
    canvas.on("selection:updated", handleSelection);
    canvas.on("selection:cleared", handleSelectionCleared);

    return () => {
      canvas.off("selection:created", handleSelection);
      canvas.off("selection:updated", handleSelection);
      canvas.off("selection:cleared", handleSelectionCleared);
    };
  }, [fabricRef]);

  // --- 드래그 이동: 위치 업데이트 ---
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isReady) return;

    const handleModified = (e: any) => {
      const target = e.target;
      if (!target) return;

      if (target instanceof CameraObject) {
        const nextPoint = pixelToPercent({
          x: target.left!,
          y: target.top!,
        });
        // 조정이 있는 카메라는 메인 = 조정 위치 → onAdjustmentMoved. 아니면 일반 매핑 위치 변경
        const hasAdjustment = adjustmentsRef.current?.has(target.cameraId) ?? false;
        if (hasAdjustment) {
          onAdjustmentMovedRef.current?.(target.cameraId, {
            x: nextPoint.x,
            y: nextPoint.y,
            rotation: target.angle,
          });
        } else {
          callbacksRef.current.onCameraPlacedOnFloorPlan(target.cameraId, {
            x: nextPoint.x,
            y: nextPoint.y,
            rotation: target.angle,
          });
        }
      } else if (target instanceof MemoMarker) {
        const nextPoint = pixelToPercent({
          x: target.left!,
          y: target.top!,
        });
        callbacksRef.current.onMemoDragEnd(target.memoId, {
          x: nextPoint.x,
          y: nextPoint.y,
        });
      }
    };

    canvas.on("object:modified", handleModified);

    return () => {
      canvas.off("object:modified", handleModified);
    };
  }, [fabricRef, isReady, pixelToPercent]);

  // --- 선택 상태 포커스 동기화 (별도 effect — 깜빡임 방지) ---
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isReady) return;

    let activeTarget: CameraObject | null = null;
    let focusChanged = false;

    canvas.getObjects().forEach((obj) => {
      if (obj instanceof CameraObject && !obj.isGhost) {
        const isFocused = obj.cameraId === selectedCameraId;
        if (obj.isFocused !== isFocused) {
          obj.updateAppearance({ isFocused });
          focusChanged = true;
        }
        if (isFocused) activeTarget = obj;
      }
      // 카메라 선택 시 메모 포커스 해제
      if (obj instanceof MemoMarker && selectedCameraId != null && obj.isFocused) {
        obj.updateAppearance({ isFocused: false });
        focusChanged = true;
      }
    });

    const currentActive = canvas.getActiveObject();
    isProgrammaticSelectionRef.current = true;
    if (activeTarget) {
      if (currentActive !== activeTarget) {
        canvas.setActiveObject(activeTarget);
        focusChanged = true;
      }
    } else if (currentActive instanceof CameraObject) {
      canvas.discardActiveObject();
      focusChanged = true;
    }
    isProgrammaticSelectionRef.current = false;

    if (focusChanged) {
      canvas.requestRenderAll();
    }
  }, [selectedCameraId, isReady, fabricRef]);

  // --- 선택된 카메라 마커 위 floating 툴바 위치 동기화 ---
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isReady) return;

    const syncPosition = () => {
      const id = selectedCameraIdRef.current;
      const el = markerToolbarRef.current;
      if (!el || id == null) return;
      const obj = canvas
        .getObjects()
        .find(
          (o): o is CameraObject => o instanceof CameraObject && !o.isGhost && o.cameraId === id,
        );
      if (!obj) return;
      // 회전과 무관하게 마커 중심 + 미회전 반사이즈를 기준 (다이아몬드 bbox 회피)
      // viewport transform 직접 적용 — getBoundingRect 캐시 우회로 pan/zoom 즉시 추종
      const vpt = canvas.viewportTransform;
      const centerX = obj.left! * vpt[0] + vpt[4];
      const centerY = obj.top! * vpt[3] + vpt[5];
      const halfW = (obj.getScaledWidth() * vpt[0]) / 2;
      const halfH = (obj.getScaledHeight() * vpt[3]) / 2;
      // 회전 quadrant 별로 FOV 와 반대 방향에 toolbar 배치 — 어느 방향으로 회전해도 가림 없음 (PO QA d)
      const angle = ((obj.angle ?? 0) % 360 + 360) % 360;
      const quadrant = Math.floor(((angle + 45) % 360) / 90); // 0=N, 1=E, 2=S, 3=W
      const gap = 6;
      let left: number;
      let top: number;
      let transform: string;
      if (quadrant === 0) {
        // FOV 북 → toolbar 남
        left = centerX;
        top = centerY + halfH + gap;
        transform = "translate(-50%, 0)";
      } else if (quadrant === 1) {
        // FOV 동 → toolbar 서
        left = centerX - halfW - gap;
        top = centerY;
        transform = "translate(-100%, -50%)";
      } else if (quadrant === 2) {
        // FOV 남 → toolbar 북
        left = centerX;
        top = centerY - halfH - gap;
        transform = "translate(-50%, -100%)";
      } else {
        // FOV 서 → toolbar 동
        left = centerX + halfW + gap;
        top = centerY;
        transform = "translate(0, -50%)";
      }
      el.style.left = `${left}px`;
      el.style.top = `${top}px`;
      el.style.transform = transform;
    };

    const updateState = () => {
      const id = selectedCameraIdRef.current;
      // 메모가 같이 선택돼 있으면 카메라 마커 toolbar 는 띄우지 않는다 — 사이드바에서 메모 작업 중인 컨텍스트
      const memoId = selectedMemoIdRef.current;
      if (id == null || memoId != null) {
        setMarkerToolbar((prev) => (prev === null ? prev : null));
        return;
      }
      const obj = canvas
        .getObjects()
        .find(
          (o): o is CameraObject => o instanceof CameraObject && !o.isGhost && o.cameraId === id,
        );
      if (!obj) {
        setMarkerToolbar((prev) => (prev === null ? prev : null));
        return;
      }
      const camera = camerasOnFloorPlanRef.current.find((c) => c.id === id);
      const isAnalyzed = isCameraAnalyzed(camera?.analysisNumber ?? null);
      setMarkerToolbar((prev) => {
        if (prev && prev.cameraId === id && prev.isAnalyzed === isAnalyzed) {
          return prev;
        }
        return { cameraId: id, isAnalyzed };
      });
      syncPosition();
    };

    updateState();

    // 위치는 ref+DOM 직접 갱신 (React re-render 우회, pan/drag 60fps 추종)
    canvas.on("after:render", syncPosition);
    canvas.on("object:moving", syncPosition);

    return () => {
      canvas.off("after:render", syncPosition);
      canvas.off("object:moving", syncPosition);
    };
  }, [selectedCameraId, selectedMemoId, camerasOnFloorPlan, isReady, fabricRef]);

  // --- 선택된 메모 마커 위 floating 삭제 툴바 위치 동기화 ---
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isReady) return;

    const syncPosition = () => {
      const id = selectedMemoId;
      const el = memoToolbarRef.current;
      if (!el || id == null) return;
      const obj = canvas
        .getObjects()
        .find((o): o is MemoMarker => o instanceof MemoMarker && o.memoId === id);
      if (!obj) return;
      const vpt = canvas.viewportTransform;
      const centerX = obj.left! * vpt[0] + vpt[4];
      const centerY = obj.top! * vpt[3] + vpt[5];
      const halfW = (obj.getScaledWidth() * vpt[0]) / 2;
      const halfH = (obj.getScaledHeight() * vpt[3]) / 2;
      el.style.left = `${centerX - halfW}px`;
      el.style.top = `${centerY - halfH}px`;
      // 메모 마커를 가리지 않도록 좌상단 외곽으로 offset
      el.style.transform = "translate(calc(-100% + 6px), calc(-100% + 6px))";
    };

    if (selectedMemoId == null) {
      setMemoToolbar((prev) => (prev === null ? prev : null));
      return;
    }
    const obj = canvas
      .getObjects()
      .find((o): o is MemoMarker => o instanceof MemoMarker && o.memoId === selectedMemoId);
    if (!obj) {
      setMemoToolbar((prev) => (prev === null ? prev : null));
      return;
    }
    setMemoToolbar((prev) =>
      prev && prev.memoId === selectedMemoId ? prev : { memoId: selectedMemoId },
    );
    syncPosition();

    canvas.on("after:render", syncPosition);
    canvas.on("object:moving", syncPosition);
    return () => {
      canvas.off("after:render", syncPosition);
      canvas.off("object:moving", syncPosition);
    };
  }, [selectedMemoId, memosOnFloorPlan, isReady, fabricRef]);

  // --- 메모 선택 상태 포커스 동기화 (사이드바 → 캔버스) ---
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isReady) return;

    let activeTarget: MemoMarker | null = null;
    let focusChanged = false;

    canvas.getObjects().forEach((obj) => {
      if (obj instanceof MemoMarker) {
        const isFocused = obj.memoId === selectedMemoId;
        if (obj.isFocused !== isFocused) {
          obj.updateAppearance({ isFocused });
          focusChanged = true;
        }
        if (isFocused) activeTarget = obj;
      }
    });

    const currentActive = canvas.getActiveObject();
    isProgrammaticSelectionRef.current = true;
    if (activeTarget) {
      if (currentActive !== activeTarget) {
        canvas.setActiveObject(activeTarget);
        focusChanged = true;
      }
    } else if (currentActive instanceof MemoMarker) {
      canvas.discardActiveObject();
      focusChanged = true;
    }
    isProgrammaticSelectionRef.current = false;

    if (focusChanged) {
      canvas.requestRenderAll();
    }
  }, [selectedMemoId, isReady, fabricRef]);

  // --- 카메라 배치 모드 (도면에 없는 카메라 선택 시) ---
  // useLayoutEffect — 브라우저 paint 이전에 canvas.hoverCursor / defaultCursor /
  // upperCanvasEl.style.cursor 가 커밋되도록. useEffect 면 mode 전환 직후 1 프레임 동안
  // 이전 커서가 보이는 race 가 생김 (PO QA 2차 i). fabric 객체들은 hoverCursor=null 이라
  // 매 mousemove 마다 canvas 값으로 동적 폴백 — 캔버스가 SOT.
  useLayoutEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isReady) return;

    if (memoPlacingMode) {
      // 이 분기는 declaration 순서상 memo placing effect 가 곧 덮어쓰지만,
      // 평소 cursor SOT(=grab) 와 일관되게 둠.
      canvas.defaultCursor = "grab";
      canvas.hoverCursor = "move";
      canvas.setCursor("grab");
      return;
    }

    // inPlacingMode 는 컴포넌트 본문 useMemo 에서 계산됨 (pan 비활성과 SOT 공유).
    if (inPlacingMode) {
      // 카메라 배치 모드 — 클릭 시 떠 있을 카메라 마커와 동일한 SVG 커서로 예측 가능성 ↑ (PO QA h ③)
      const cameraSvg = `<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 71 57'><path d='M60.7423 13.4864C55.5881 11.2793 49.9918 10.2991 44.3943 10.6231C38.7968 10.947 33.3509 12.5663 28.4858 15.3533C23.6206 18.1403 19.469 22.0189 16.3581 26.6836C13.2471 31.3483 11.2618 36.6716 10.5585 42.2342L46.4868 46.7769L60.7423 13.4864Z' fill='%23696F8C' fill-opacity='0.2'/><ellipse cx='46.487' cy='46.7769' rx='4.46012' ry='4.46012' fill='%23696F8C'/><path d='M50.8644 49.1158C49.5727 51.5333 46.5658 52.4458 44.1482 51.1541C41.7307 49.8623 40.8181 46.8555 42.1099 44.4379C43.4018 42.0205 46.4086 41.1079 48.8261 42.3996C51.2436 43.6914 52.1561 46.6983 50.8644 49.1158Z' stroke='white' stroke-width='2' fill='none'/><path d='M60.7424 13.4863C55.5882 11.2792 49.9919 10.299 44.3944 10.623C38.7968 10.947 33.351 12.5663 28.4858 15.3533C23.6207 18.1403 19.4691 22.0189 16.3582 26.6835C13.2472 31.3482 11.2619 36.6715 10.5586 42.2341' stroke='%23696F8C' stroke-width='2' fill='none'/></svg>`;
      const cameraCursorUrl = `data:image/svg+xml;utf8,${cameraSvg}`;
      // 핫스팟: 카메라 렌즈 중심 (SVG viewBox 의 46.5,46.8 → 32x32 픽셀에서 약 21,26)
      const cameraCursor = `url("${cameraCursorUrl}") 21 26, crosshair`;
      canvas.defaultCursor = cameraCursor;
      canvas.hoverCursor = cameraCursor;
      canvas.setCursor(cameraCursor);
    } else {
      // pan 가능을 알리는 grab 커서 (PO QA h ②)
      canvas.defaultCursor = "grab";
      canvas.hoverCursor = "move";
      // 배치 직후엔 mouse:move 가 없어 fabric 가 커서를 갱신하지 않음 — 즉시 강제 갱신 (PO QA 2차 i)
      canvas.setCursor("grab");
    }
  }, [inPlacingMode, isReady, memoPlacingMode, fabricRef]);

  // 배치 클릭 핸들러 (카메라 + 메모)
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isReady) return;

    const handleMouseDown = (opt: any) => {
      if (opt.target && !inPlacingModeRef.current) return;
      if (opt.e.button !== 0) return;

      const pointer = canvas.getScenePoint(opt.e);

      const currentMemoPlacingMode = memoPlacingModeRef.current;
      if (currentMemoPlacingMode) {
        const percent = pixelToPercent({ x: pointer.x, y: pointer.y });
        callbacksRef.current.onMemoPlaced(currentMemoPlacingMode.memoId, {
          x: percent.x,
          y: percent.y,
        });
        // 배치 직후 cursor 를 즉시 grab 으로 reset — React 상태 갱신 race 의존성 제거 (PO QA 3차 w)
        canvas.defaultCursor = "grab";
        canvas.hoverCursor = "move";
        canvas.setCursor("grab");
        return;
      }

      const currentSelectedCameraId = selectedCameraIdRef.current;
      const currentSelectedMemoId = selectedMemoIdRef.current;
      const currentCamerasOnFloor = camerasOnFloorPlanRef.current;
      const isSelectedCameraOnFloorPlan =
        currentSelectedCameraId != null &&
        currentCamerasOnFloor.some((c) => c.id === currentSelectedCameraId);

      // 메모 선택 중이면 카메라 배치 액션 발동 안 함. 또 다른 도면에 이미 매핑된 카메라
      // 를 선택한 채 빈 영역 클릭해도 placement 액션 발동 안 함 — 신규(미배치) 일 때만.
      if (
        currentSelectedMemoId == null &&
        currentSelectedCameraId != null &&
        !isSelectedCameraOnFloorPlan &&
        selectedCameraIsUnplacedRef.current
      ) {
        const percent = pixelToPercent({ x: pointer.x, y: pointer.y });
        callbacksRef.current.onCameraPlacedOnFloorPlan(
          currentSelectedCameraId,
          { x: percent.x, y: percent.y, rotation: 0 },
        );
        // 배치 직후 cursor 를 즉시 grab 으로 reset — React 상태 갱신 race 의존성 제거 (PO QA 2차 i)
        canvas.defaultCursor = "grab";
        canvas.hoverCursor = "move";
        canvas.setCursor("grab");
      }
    };

    canvas.on("mouse:down", handleMouseDown);
    return () => {
      canvas.off("mouse:down", handleMouseDown);
    };
  }, [fabricRef, isReady, pixelToPercent]);

  // --- 겹친 카메라 순환 선택 (개선 니즈 #2 2단계) ---
  //
  // 부채꼴은 투명도 20% 로 칠해진 면이라 이웃 카메라끼리 자주 겹친다. 겹친 지점은 양쪽 다
  // "보이는 그림"이라 픽셀 판정으로도 구분되지 않고, fabric 은 z-order 상 맨 위만 고르므로
  // 아래 깔린 카메라는 영원히 선택할 수 없었다.
  //
  // fabric 의 findTarget 은 "이 포인터 이벤트의 대상이 무엇인가"를 정하는 단일 지점이다.
  // 선택·드래그·hover·커서가 모두 이 결과를 쓰므로, 여기서 고르면 사이드바와 캔버스 마커가
  // 따로 놀 수 없다. (mouse:up 에서 선택만 바꾸면 fabric 이 자기 대상으로 되돌리거나,
  //  앱 상태만 바뀌고 캔버스 마커는 그대로 남는다)
  //
  // 규칙: 후보가 여럿이면 dot 이 가까운 순으로 세우고, 그중 지금 선택된 카메라가 있으면
  // 그 다음 것을 대상으로 한다. 같은 자리를 반복해 누르면 아래 깔린 카메라까지 차례로 닿는다.
  // hover 중에는 선택이 바뀌지 않으므로 대상도 안정적이다.
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isReady) return;

    const target = canvas;
    type FindTarget = (e: TPointerEvent) => ReturnType<typeof target.findTarget>;
    const original = target.findTarget.bind(target) as FindTarget;

    const patched: FindTarget = (e) => {
      const info = original(e);
      if (inPlacingModeRef.current) return info;

      const active = canvas.getActiveObject();
      // 활성 객체의 컨트롤(회전 핸들)을 잡은 경우는 fabric 판단을 그대로 둔다
      if (active?.findControl?.(canvas.getViewportPoint(e))) return info;

      const pointer = canvas.getScenePoint(e);
      const currentCameraId = active instanceof CameraObject ? active.cameraId : null;
      const next = pickCameraAt(canvas, pointer, currentCameraId);
      if (!next || next === info.target) return info;

      return { ...info, target: next, currentTarget: next };
    };

    target.findTarget = patched;
    return () => {
      target.findTarget = original;
    };
  }, [fabricRef, isReady]);

  // --- 메모 배치 모드 커서 ---
  // 클릭 시 떠 있을 메모 마커와 동일한 모양의 SVG 커서로 사용자가 결과를 예측하도록 함 (피드백)
  // 비-메모 모드(카메라 placing / 모드 없음)의 커서는 카메라 placing 모드 effect 가 SOT 로 관리.
  // 같은 render 안에서 두 effect 가 stale state 를 읽으며 서로 덮어쓰는 race 를 막기 위함 (PO QA 2차 i)
  // useLayoutEffect — 위 카메라 placing 효과(declaration 순서상 먼저)가 먼저 commit 된 뒤
  // 이 효과가 동기적으로 덮어써야 memo placing SVG cursor 가 paint 전에 반영됨.
  useLayoutEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isReady) return;

    if (memoPlacingMode) {
      // MemoMarker 실제 렌더링과 동일한 SVG — 둥근 사각형 + 3-line 메모 아이콘. 색상 #696F8C (default 상태).
      const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'><rect x='1' y='1' width='22' height='22' rx='4' fill='rgba(255,255,255,0.48)' stroke='%23696F8C' stroke-width='2'/><svg x='5' y='5' width='14' height='14' viewBox='0 0 16 16'><path d='M16 16H0V0H16V16ZM3.2002 10.4004V12H12.8008V10.4004H3.2002ZM3.2002 7.2002V8.7998H12.8008V7.2002H3.2002ZM3.2002 4V5.60059H9.60059V4H3.2002Z' fill='%23696F8C'/></svg></svg>`;
      const url = `data:image/svg+xml;utf8,${svg}`;
      // 핫스팟: 마커 중심(12,12)
      canvas.defaultCursor = `url("${url}") 12 12, crosshair`;
      canvas.setCursor(`url("${url}") 12 12, crosshair`);
    }
  }, [memoPlacingMode, isReady, fabricRef]);

  // --- 선택된 개체 삭제 ---
  const requestDeleteActiveRenderable = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const active = canvas.getActiveObject();
    let memoId: number | null = null;
    let cameraId: number | null = null;
    if (active) {
      // active 가 있으면 그 타입만 처리. 다른 종류(예: 도면 메모 텍스트/사각형) 가 선택돼 있으면
      // 별도 핸들러가 처리하므로 여기서 selectedCameraId 등으로 fallback 하지 않는다 (j 버그)
      if (active instanceof CameraObject) {
        cameraId = active.cameraId;
      } else if (active instanceof MemoMarker) {
        memoId = active.memoId;
      } else {
        return;
      }
    } else {
      // 빈 영역 클릭으로 fabric 이 discard 한 경우만 선택 상태로 fall back
      if (selectedMemoId != null && memosOnFloorPlan.some((m) => m.id === selectedMemoId)) {
        memoId = selectedMemoId;
      } else if (selectedCameraId != null && camerasOnFloorPlan.some((c) => c.id === selectedCameraId)) {
        cameraId = selectedCameraId;
      }
    }

    if (cameraId != null) {
      const camera = camerasOnFloorPlan.find((c) => c.id === cameraId);
      if (isCameraAnalyzed(camera?.analysisNumber ?? null)) {
        setShowDeleteBlockedDialog(true);
        return;
      }
      const name = nonAnalysisNumberMap.get(cameraId) ?? camera?.defaultCamId ?? String(cameraId);
      setDeleteConfirm({ type: "camera", cameraId, name });
    } else if (memoId != null) {
      setDeleteConfirm({ type: "memo", memoId });
    }
  }, [camerasOnFloorPlan, memosOnFloorPlan, nonAnalysisNumberMap, fabricRef, selectedMemoId, selectedCameraId]);

  // Backspace/Delete 키 삭제, Escape 키로 마커 툴바 닫기(= 선택 해제, PO QA d)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (target?.isContentEditable) return;
      // 도면 요소(텍스트/사각형) 삭제 핸들러가 먼저 처리했으면 (preventDefault) 더 진행하지 않음.
      // 그렇지 않으면 active 가 이미 discard 된 상태로 selectedCameraId fallback 분기로 빠져
      // "카메라 삭제 불가" 모달 등 잘못된 분기가 발동함 (PO QA j 회귀 fix).
      if (e.defaultPrevented) return;
      if (e.key === "Backspace" || e.key === "Delete") {
        requestDeleteActiveRenderable();
      } else if (e.key === "Escape") {
        const canvas = fabricRef.current;
        if (canvas) {
          canvas.discardActiveObject();
          canvas.requestRenderAll();
        }
        onSelectionCleared();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [requestDeleteActiveRenderable, fabricRef, onSelectionCleared]);

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteConfirm) return;
    if (deleteConfirm.type === "camera") {
      const canvas = fabricRef.current;
      if (canvas) {
        // discardActiveObject 시 selection:cleared 이벤트 발생 방지
        isProgrammaticSelectionRef.current = true;
        canvas.discardActiveObject();
        isProgrammaticSelectionRef.current = false;

        // Fabric 캔버스에서 즉시 제거
        // 메인 + ghost 양쪽 다 제거 (delete 시 두 마커 모두 사라져야)
        const cameraObjs = canvas
          .getObjects()
          .filter(
            (o): o is CameraObject =>
              o instanceof CameraObject && o.cameraId === deleteConfirm.cameraId,
          );
        cameraObjs.forEach((obj) => canvas.remove(obj));
        canvas.requestRenderAll();
      }
      onDeleteCamera(deleteConfirm.cameraId);
    } else if (deleteConfirm.type === "memo") {
      const canvas = fabricRef.current;
      if (canvas) {
        isProgrammaticSelectionRef.current = true;
        canvas.discardActiveObject();
        isProgrammaticSelectionRef.current = false;

        const marker = canvas
          .getObjects()
          .find(
            (o): o is MemoMarker =>
              o instanceof MemoMarker && o.memoId === deleteConfirm.memoId,
          );
        if (marker) {
          canvas.remove(marker);
        }
        canvas.requestRenderAll();
      }
      onDeleteMemoFromFloorPlan(deleteConfirm.memoId);
    } else {
      // floor-plan-memo (텍스트/사각형) — 확인 후 fabric 제거 + pending change 등록
      const canvas = fabricRef.current;
      if (canvas) {
        const target = canvas
          .getObjects()
          .find(
            (o) =>
              isFloorPlanMemoObject(o)
              && o.__memoId === deleteConfirm.memoId
              && o.__memoKind === deleteConfirm.memoKind,
          );
        if (target) {
          canvas.discardActiveObject();
          canvas.remove(target);
          canvas.requestRenderAll();
        }
      }
      if (deleteConfirm.memoKind === "diagram") {
        onMemoDiagramDelete?.(deleteConfirm.memoId, deleteConfirm.floorPlanId);
      } else {
        onMemoTextDelete?.(deleteConfirm.memoId, deleteConfirm.floorPlanId);
      }
    }
    setDeleteConfirm(null);
  }, [deleteConfirm, onDeleteCamera, onDeleteMemoFromFloorPlan, onMemoDiagramDelete, onMemoTextDelete, fabricRef]);

  return (
    <Container>
      <CanvasWrapper ref={containerRef}>
        <canvas ref={canvasRef} />
      </CanvasWrapper>

      {markerToolbar && (
        <MarkerToolbar
          ref={(el) => {
            markerToolbarRef.current = el;
            // 마운트 직후 한 번 위치 동기화 (after:render 기다리지 않도록)
            if (el) {
              const canvas = fabricRef.current;
              if (!canvas) return;
              const obj = canvas
                .getObjects()
                .find(
                  (o): o is CameraObject =>
                    o instanceof CameraObject && !o.isGhost && o.cameraId === markerToolbar.cameraId,
                );
              if (!obj) return;
              const vpt = canvas.viewportTransform;
              const centerX = obj.left! * vpt[0] + vpt[4];
              const centerY = obj.top! * vpt[3] + vpt[5];
              const halfW = (obj.getScaledWidth() * vpt[0]) / 2;
              const halfH = (obj.getScaledHeight() * vpt[3]) / 2;
              const angle = ((obj.angle ?? 0) % 360 + 360) % 360;
              const quadrant = Math.floor(((angle + 45) % 360) / 90);
              const gap = 6;
              let left: number;
              let top: number;
              let transform: string;
              if (quadrant === 0) {
                left = centerX;
                top = centerY + halfH + gap;
                transform = "translate(-50%, 0)";
              } else if (quadrant === 1) {
                left = centerX - halfW - gap;
                top = centerY;
                transform = "translate(-100%, -50%)";
              } else if (quadrant === 2) {
                left = centerX;
                top = centerY - halfH - gap;
                transform = "translate(-50%, -100%)";
              } else {
                left = centerX + halfW + gap;
                top = centerY;
                transform = "translate(0, -50%)";
              }
              el.style.left = `${left}px`;
              el.style.top = `${top}px`;
              el.style.transform = transform;
            }
          }}
        >
          {(() => {
            const isEditing = adjustmentEditingCameraId === markerToolbar.cameraId;
            const camera = camerasOnFloorPlan.find((c) => c.id === markerToolbar.cameraId);
            // PO QA 4차 af: 두 페이지 (analysis-draft / camera-mapping) 의 스냅샷 판정식 통합 (entities/camera/utils#hasRealSnapshot)
            const realSnapshot = !!camera && hasRealSnapshot(camera);
            const disabled = !isEditing && !realSnapshot;
            const adjustmentLabel = isEditing
              ? t("adjustment.end")
              : disabled
                ? t("adjustment.startDisabledNoSnapshot")
                : t("adjustment.start");
            // 조정 모드 진입 시엔 아이콘 버튼이 아니라 "조정 완료" 텍스트 버튼으로 확장 — 다음 액션을 명확히 안내 (PO QA 2차 j-1)
            if (isEditing) {
              return (
                <AdjustmentEndButton
                  type="button"
                  onClick={() => onEndAdjustment?.()}
                  aria-label={adjustmentLabel}
                >
                  <Icon icon="ic_chart_default" width={16} height={16} />
                  <span>{adjustmentLabel}</span>
                </AdjustmentEndButton>
              );
            }
            return (
              <Tooltip
                trigger={
                  <MarkerToolbarButton
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      if (disabled) return;
                      onStartAdjustment?.(markerToolbar.cameraId);
                    }}
                    aria-label={adjustmentLabel}
                  >
                    <Icon icon="ic_chart_default" width={16} height={16} />
                  </MarkerToolbarButton>
                }
                side="top"
                sideOffset={4}
              >
                {adjustmentLabel}
              </Tooltip>
            );
          })()}
          <Tooltip
            trigger={
              <MarkerToolbarButton
                type="button"
                onClick={() => onAddMemo?.(markerToolbar.cameraId)}
                aria-label={t("canvas.addMemo")}
              >
                <Icon icon="ic_chart_description" width={16} height={16} />
              </MarkerToolbarButton>
            }
            side="top"
            sideOffset={4}
          >
            {t("canvas.addMemo")}
          </Tooltip>
          <Tooltip
            trigger={
              <MarkerToolbarButton
                type="button"
                $destructive={!markerToolbar.isAnalyzed}
                disabled={markerToolbar.isAnalyzed}
                onClick={requestDeleteActiveRenderable}
                aria-label={t(
                  markerToolbar.isAnalyzed
                    ? "canvas.removeFromFloorPlanDisabled"
                    : "canvas.removeFromFloorPlan",
                )}
              >
                <IcEraser width={16} height={16} />
              </MarkerToolbarButton>
            }
            side="top"
            sideOffset={4}
          >
            {markerToolbar.isAnalyzed
              ? t("canvas.removeFromFloorPlanDisabled")
              : `${t("canvas.removeFromFloorPlan")} (Backspace)`}
          </Tooltip>
        </MarkerToolbar>
      )}

      {memoToolbar && (
        <MarkerToolbar
          ref={(el) => {
            memoToolbarRef.current = el;
            if (el) {
              const canvas = fabricRef.current;
              if (!canvas) return;
              const obj = canvas
                .getObjects()
                .find(
                  (o): o is MemoMarker =>
                    o instanceof MemoMarker && o.memoId === memoToolbar.memoId,
                );
              if (!obj) return;
              const vpt = canvas.viewportTransform;
              const centerX = obj.left! * vpt[0] + vpt[4];
              const centerY = obj.top! * vpt[3] + vpt[5];
              const halfW = (obj.getScaledWidth() * vpt[0]) / 2;
              const halfH = (obj.getScaledHeight() * vpt[3]) / 2;
              // 메모 마커는 회전 안 함. 위쪽 좌상단 외곽에 toolbar 배치 (마커 자체를 가리지 않게 transform).
              el.style.left = `${centerX - halfW}px`;
              el.style.top = `${centerY - halfH}px`;
              el.style.transform = "translate(calc(-100% + 6px), calc(-100% + 6px))";
            }
          }}
        >
          <Tooltip
            trigger={
              <MarkerToolbarButton
                type="button"
                $destructive
                onClick={requestDeleteActiveRenderable}
                aria-label={t("canvas.removeFromFloorPlan")}
              >
                <IcEraser width={16} height={16} />
              </MarkerToolbarButton>
            }
            side="top"
            sideOffset={4}
          >
            {`${t("canvas.removeFromFloorPlan")} (Backspace)`}
          </Tooltip>
        </MarkerToolbar>
      )}

      {floorPlans.length > 0 && (
        <SelectWrapper>
          <FpSelect ref={fpSelectRef}>
            <FpSelectTrigger type="button" onClick={() => setFpSelectOpen((v) => !v)}>
              <span>{selectedFloorPlan?.name || t("canvas.selectFloorPlan")}</span>
              <Icon icon={fpSelectOpen ? "ic_chevron_up" : "ic_chevron_down"} width={16} />
            </FpSelectTrigger>
            {fpSelectOpen && (
              <FpSelectMenu>
                {floorPlans.map((fp) => (
                  <FpSelectItem
                    key={fp.id}
                    type="button"
                    $selected={fp.id === selectedFloorPlanId}
                    onClick={() => { onFloorPlanChange(fp.id); setFpSelectOpen(false); }}
                  >
                    {fp.name}
                  </FpSelectItem>
                ))}
              </FpSelectMenu>
            )}
          </FpSelect>
          {selectedFloorPlanId != null && (onMemoTextCreate || onMemoDiagramCreate) && (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <AddElementButton type="button" $active={memoCreateMode !== null}>
                  {t("canvas.addFloorPlanElement")}
                </AddElementButton>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <AddElementMenuContent sideOffset={4} align="start">
                  {onMemoTextCreate && (
                    <AddElementMenuItem onSelect={() => setMemoCreateMode("text")}>
                      {t("canvas.addMemoText")}
                    </AddElementMenuItem>
                  )}
                  {onMemoDiagramCreate && (
                    <AddElementMenuItem onSelect={() => setMemoCreateMode("diagram")}>
                      {t("canvas.addMemoDiagram")}
                    </AddElementMenuItem>
                  )}
                </AddElementMenuContent>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          )}
          {selectedFloorPlanId != null
            && ((floorPlanMemoDiagrams?.length ?? 0) > 0 || (floorPlanMemoTexts?.length ?? 0) > 0) && (
            <SwitchRow>
              <SwitchLabel>{t("canvas.showFloorPlanMemo")}</SwitchLabel>
              <Switch
                size="small"
                color="primary"
                checked={showFloorPlanMemos}
                onCheckedChange={(v) => { if (typeof v === "boolean") setShowFloorPlanMemos(v); }}
              />
            </SwitchRow>
          )}
        </SelectWrapper>
      )}

      {selectedMemo && (
        <EditPanelWrapper>
          <FloorPlanMemoEditPanel
            memo={selectedMemo}
            onTextStyleChange={handleTextStyleChange}
            onDiagramStyleChange={handleDiagramStyleChange}
          />
        </EditPanelWrapper>
      )}

      <ToolButtons>
        <Tooltip trigger={
          <ToolButton onClick={() => setShowIconScalePopover((prev) => !prev)}>
            <ResizeCameraIcon />
          </ToolButton>
        } side="top" sideOffset={4}>
          {t("canvas.cameraScaleTitle")}
        </Tooltip>
        <ToolButton onClick={handleZoomIn}>
          <Icon icon="ic_zoom_in" width={20} fill="none" stroke="currentColor" />
        </ToolButton>
        <ToolButton onClick={handleZoomOut}>
          <Icon icon="ic_zoom_out" width={20} fill="none" stroke="currentColor" />
        </ToolButton>
      </ToolButtons>

      {showIconScalePopover && (
        <CameraScalePopover
          iconScale={iconScale}
          onIconScaleChange={onIconScaleChange}
          onIconScaleCommit={onIconScaleCommit}
          labelScale={labelScale}
          onLabelScaleChange={onLabelScaleChange}
          onLabelScaleCommit={onLabelScaleCommit}
          onClose={() => setShowIconScalePopover(false)}
        />
      )}

      <DeleteConfirmDialog
        open={deleteConfirm !== null}
        title={t(
          deleteConfirm?.type === "camera"
            ? "dialog.deleteCameraTitle"
            : deleteConfirm?.type === "memo"
              ? "dialog.removeMemoFromFloorPlanTitle"
              : deleteConfirm?.type === "floor-plan-memo" && deleteConfirm.memoKind === "diagram"
                ? "dialog.deleteFloorPlanDiagramTitle"
                : "dialog.deleteFloorPlanTextTitle",
        )}
        name={deleteConfirm?.type === "camera" ? deleteConfirm.name : ""}
        messageKey=""
        message={t(
          deleteConfirm?.type === "camera"
            ? "dialog.deleteCameraMessage"
            : deleteConfirm?.type === "memo"
              ? "dialog.removeMemoFromFloorPlanMessage"
              : deleteConfirm?.type === "floor-plan-memo" && deleteConfirm.memoKind === "diagram"
                ? "dialog.deleteFloorPlanDiagramMessage"
                : "dialog.deleteFloorPlanTextMessage",
          deleteConfirm?.type === "camera" ? { name: deleteConfirm.name } : {},
        )}
        ns="camera-mapping"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm(null)}
      />


      {showDeleteBlockedDialog && (
        <DeleteBlockedDialog
          ref={(el) => {
            if (el && !el.open) el.showModal();
          }}
          onCancel={(e) => {
            e.preventDefault();
            setShowDeleteBlockedDialog(false);
          }}
        >
          <DeleteBlockedHeader>{t("dialog.deleteBlockedTitle")}</DeleteBlockedHeader>
          <DeleteBlockedBody>{t("dialog.deleteBlockedMessage")}</DeleteBlockedBody>
          <DeleteBlockedFooter>
            <Button variant="outlined" color="inherit" size="medium" onClick={() => setShowDeleteBlockedDialog(false)}>
              {t("dialog.deleteBlockedClose")}
            </Button>
          </DeleteBlockedFooter>
        </DeleteBlockedDialog>
      )}

    </Container>
  );
}

// 임시 좌표 생성
function getTempPosition(
  camera: Camera,
  allCameras: Camera[],
): { x: number; y: number } {
  const idx = allCameras.findIndex((c) => c.id === camera.id);
  const cols = Math.ceil(Math.sqrt(allCameras.length));
  const row = Math.floor(idx / cols);
  const col = idx % cols;
  const step = 80 / Math.max(cols, 1);
  return {
    x: 10 + col * step,
    y: 10 + row * step,
  };
}

// --- Styles ---

const Container = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  background-color: #fff;
`;

const CanvasWrapper = styled.div`
  width: 100%;
  height: 100%;
`;

const MarkerToolbar = styled.div`
  position: absolute;
  z-index: 12;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 4px;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 4px 12px rgba(17, 17, 31, 0.12), 0 0 1px rgba(17, 17, 31, 0.16);
  pointer-events: auto;
`;

const MarkerToolbarButton = styled.button<{ $active?: boolean; $destructive?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: ${({ $active }) =>
    $active ? "rgba(72, 75, 210, 0.16)" : "transparent"};
  color: ${({ $active }) => ($active ? "#484BD2" : token("colors.text.primary"))};
  cursor: pointer;
  transition: background 0.12s ease;

  &:hover:not(:disabled) {
    background: ${({ $active }) =>
      $active ? "rgba(72, 75, 210, 0.24)" : token("colors.action.hover")};
  }

  &:disabled {
    color: ${token("colors.text.disabled")};
    cursor: not-allowed;
  }
`;

// 조정 모드 진입 시 표시되는 강조 텍스트 버튼 — 다음 액션이 "조정 완료" 임을 명확히 노출 (PO QA 2차 j-1)
const AdjustmentEndButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 10px;
  border: none;
  border-radius: 6px;
  background: rgba(72, 75, 210, 0.16);
  color: #484BD2;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  transition: background 0.12s ease;

  &:hover {
    background: rgba(72, 75, 210, 0.24);
  }
`;

const SelectWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 12px;
  z-index: 50;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EditPanelWrapper = styled.div`
  position: absolute;
  top: 52px;
  left: 12px;
  z-index: 1000;
`;

const AddElementButton = styled.button<{ $active?: boolean }>`
  height: 32px;
  padding: 0 12px;
  border: 1px solid ${token("colors.gray.GR300")};
  border-radius: 8px;
  background: ${({ $active }) =>
    $active ? "rgba(72, 75, 210, 0.16)" : token("colors.gray.white")};
  color: ${({ $active }) => ($active ? "#484BD2" : token("colors.text.primary"))};
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  cursor: pointer;

  &:hover {
    background: ${({ $active }) =>
      $active ? "rgba(72, 75, 210, 0.24)" : token("colors.action.hover")};
  }
`;

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 32px;
`;

const SwitchLabel = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: ${token("colors.text.primary")};
  white-space: nowrap;
`;

const AddElementMenuContent = styled(DropdownMenu.Content)`
  min-width: 140px;
  background: ${token("colors.gray.white")};
  border: 1px solid ${token("colors.gray.GR300")};
  border-radius: 8px;
  padding: 4px 0;
  box-shadow: ${token("shadows.dropdown")};
  z-index: 11;
`;

const AddElementMenuItem = styled(DropdownMenu.Item)`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  cursor: pointer;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: ${token("colors.text.primary")};
  outline: none;

  &:hover,
  &[data-highlighted] {
    background: ${token("colors.action.hover")};
  }
`;

const FpSelect = styled.div`
  position: relative;
  width: 180px;
`;

const FpSelectTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  width: 100%;
  height: 32px;
  padding: 0 8px 0 12px;
  border-radius: 8px;
  border: 1px solid rgba(143, 149, 178, 0.18);
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(8px);
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  color: ${token("colors.text.primary")};
  cursor: pointer;
  box-sizing: border-box;

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    border-color: ${token("colors.brand.primary")};
  }
`;

const FpSelectMenu = styled.div`
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  z-index: 20;
  max-height: 240px;
  overflow-y: auto;
  padding: 8px 0;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #E6E8F0;
  box-shadow:
    -20px 20px 40px -4px rgba(105, 111, 140, 0.24),
    0px 0px 2px 0px rgba(105, 111, 140, 0.16);
  display: flex;
  flex-direction: column;
`;

const FpSelectItem = styled.button<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  border: none;
  background: ${({ $selected }) => ($selected ? "rgba(114, 142, 255, 0.08)" : "transparent")};
  color: ${token("colors.text.primary")};
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${({ $selected }) => ($selected ? "rgba(114, 142, 255, 0.12)" : token("colors.action.hover"))};
  }
`;

const ToolButtons = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  bottom: 60px;
  right: 12px;
  z-index: 5;
  gap: 4px;
  width: 32px;

  .tooltip-trigger-wrapper {
    display: flex;
  }
`;

const ToolButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.48);
  cursor: pointer;
  color: #242946;

  &:hover {
    background: ${token("colors.action.hover")};
  }
`;






const DeleteBlockedDialog = styled.dialog`
  position: fixed;
  inset: 0;
  width: 480px;
  height: fit-content;
  margin: auto;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0px 20px 40px -4px rgba(105, 111, 140, 0.16);

  &::backdrop {
    background: rgba(17, 17, 31, 0.32);
  }
`;

const DeleteBlockedHeader = styled.div`
  padding: 24px;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.56;
  color: ${token("colors.text.primary")};
`;

const DeleteBlockedBody = styled.div`
  padding: 0 24px;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.57;
  color: ${token("colors.text.primary")};
`;

const DeleteBlockedFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 24px;
`;
